import {
  ActionType,
  CustomAction,
  EmptyListingButtonTypes,
  ExtraFilter,
  FavoriteAccessItem,
  FilterAll,
  FilterComponent,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  ListingSelectOption,
  Operator,
  optimizedRetryOption,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
  useFavoriteAccess,
  useNavigation,
  ViewSelectOption,
} from "o4a-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  DetailsPanelId,
  EXAINFRA_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  emptyChar,
  ListingTestIds,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
  ViewKeys,
} from "../../constants/uiConstants";
import { AzureSubscriptionSummary } from "../../gen/clients/mchub-azure-api-client";
import {
  CloudExadataInfrastructureSummary,
  CloudExadataInfrastructureSummaryCollection,
  CloudExadataInfrastructureSummaryStatusEnum,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import {
  getFieldCustomOptions,
  getStatusInfo,
  responseItemstoArray,
  statusSortComparator,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFilterStatePills } from "../../hooks/useFilterStatePills";
import { useLocationFilters } from "../../hooks/useLocationFilters";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { ExaInfraDeleteProps, newExaInfraDelete } from "../../operations/ExaInfra/ExaInfraDelete";
import { getAzureLocationName, getOciRegion } from "../../utils";

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.EXAINFRA_DETAILS].map(config => config.key);

interface ExaInfraItem {
  id: string;
  displayName: string;
  subscriptionName: string;
  subscriptionId: string;
  resourceGroupName: string;
  location: string;
  status?: CloudExadataInfrastructureSummaryStatusEnum;
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
  lifecycleDetails?: string;
  freeformTags?: { [key: string]: string; };
  isFavorite: boolean;
}

interface AzureSubscriptionSummaryMap {
  [key: string]: AzureSubscriptionSummary;
}

export enum ColumnIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  FreeformTags = "freeformTags",
}

export enum ColumnTestIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  FreeformTags = "freeformTags",
}

export enum ActionBarIds {
  Create = "action-bar-create",
  Refresh = "action-bar-refresh",
  Delete = "action-bar-delete"
}

export enum ActionMenuIds {
  Delete = "action-menu-delete",
  AddFavorite = "action-menu-add-favorite",
  RemoveFavorite = "action-menu-remove-favorite",
}

export enum ActionBarTestIds {
  Create = "action-bar-create",
  Refresh = "action-bar-refresh",
  Delete = "action-bar-delete",
}

export enum ActionMenuTestIds {
  Delete = "action-menu-delete",
  AddFavorite = "action-menu-add-favorite",
  RemoveFavorite = "action-menu-remove-favorite",
}

export enum FilterTestIds {
  SubscriptionFilter = "subscription-filter",
  LocationFilter = "location-filter",
}

export interface ExaInfraListProps {
  disabled?: boolean;
}

export const ExaInfraList = ({ disabled }: ExaInfraListProps): JSX.Element => {
  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { favoriteItems, isFavorite, setAsFavorite, removeFromFavorites } = useFavoriteAccess();

  const { trigger: triggerDeleteExaInfra } = useOperation<ExaInfraDeleteProps>(newExaInfraDelete);
  const { closePanels } = useSidePanel();

  const { navigateTo } = useNavigation(ConsoleContext);
  // eslint-disable-next-line max-len
  const { preferredSubscription, locale } = React.useContext<Settings>(SettingsContext);
  const filterState = React.useContext<FilterState>(FilterStateContext);
  const { listId } = useParams();

  const [selection, setSelection] = React.useState<ExaInfraItem[]>([]);
  const [selectedSubscription, setSelectedSubscription] = React.useState<string>("");
  const [subscriptionOptions, setSubscriptionOptions] = React.useState<{ key: string, text: string }[]>([]);

  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();

  const { locationOptions, selectedLocation, setSelectedLocation } = useLocationFilters(
    filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
  );

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent<ExaInfraItem>>();

  const [filterComponentRef, setFilterComponentRef] = React.useState<FilterComponent>();
  const pillFilterValues = filterComponentRef?.getFilters();
  const filterTextValue = filterComponentRef?.getFilterText();
  const [filterText, setFilterText] = React.useState(filterState.filterTextValue || filterTextValue);

  const { perPageItemsCount } = useRowCount();

  const groupingOptions: ListingSelectOption<ExaInfraItem>[] = [];

  const viewOptions: ViewSelectOption<ExaInfraItem>[] = [
    {
      key: ViewKeys.ListView,
      text: Messages.listExaInfra.filters.listView(),
      icon: MonochromeIconIds.List,
    },
  ];

  const getLocation = (value: ExaInfraItem): string => getAzureLocationName(value.location);

  const getStatus = (value: ExaInfraItem): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
          value?.lifecycleDetails,
        )
      }
      hideClipboardCopy
    />
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.DisplayName,
      testId: ColumnTestIds.DisplayName,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.displayName}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.EXAINFRA_DETAILS][0].panelPath}?location=${value.location}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
          iconName={SvgIconIds.infraSvg}
        />
      ),
    },
    {
      itemProp: ColumnIds.SubscriptionName,
      testId: ColumnTestIds.SubscriptionName,
      name: Messages.labels.subscription(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components, max-len
      onRenderItems: value => <AzureSubscriptionLink resourceId={value.id} subscriptionName={value.subscriptionName} hideClipboard />,
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} hideClipboard />,
    },
    {
      itemProp: ColumnIds.Location,
      testId: ColumnTestIds.Location,
      name: Messages.labels.location(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: ExaInfraItem,
        b: ExaInfraItem,
      ) => statusSortComparator<ExaInfraItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
  ];

  const {
    refresh: exaInfraRefresh,
    loading: exaInfraLoading,
    response: exaInfraResponse,
  } = useQueryCall({
    wait: (!selectedSubscription || !selectedLocation) || disabled,
    method: apiClients.withRegion(
      getOciRegion(selectedLocation),
    ).exaDatabaseApi.listCloudExadataInfrastructuresBySubscription,
    options: {
      args: {
        subscriptionId: selectedSubscription,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExadbInfra(),
      },
    },
  });

  // eslint-disable-next-line max-len
  const exaInfras = exaInfraResponse && responseItemstoArray<CloudExadataInfrastructureSummaryCollection, CloudExadataInfrastructureSummary>(exaInfraResponse);

  const items = React.useMemo(() => {
    const newExaInfras: ExaInfraItem[] = [];

    if (exaInfras && subscriptions && subscriptions.length > 0) {
      const subscriptionsMap = subscriptions.reduce((
        map: AzureSubscriptionSummaryMap,
        subscription: AzureSubscriptionSummary,
      ) => {
        map[subscription.id] = subscription;
        return map;
      }, {});
      exaInfras.forEach(exaInfra => {
        if (exaInfra.lifecycleState !== "TERMINATED") {
          const { subscriptionId, resourceGroup, resourceName } = parseId(exaInfra.id);
          const exaInfraSubscriptionName = subscriptionsMap && subscriptionsMap[subscriptionId]?.name;
          newExaInfras.push({
            id: exaInfra.id,
            displayName: exaInfra.name || resourceName,
            subscriptionName: exaInfraSubscriptionName,
            subscriptionId,
            resourceGroupName: resourceGroup,
            location: exaInfra.location,
            status: exaInfra.status,
            lifecycleDetails: exaInfra.lifecycleDetails,
            freeformTags: exaInfra.freeformTags,
            isFavorite: isFavorite(exaInfra.id),
          });
        }
      });
    }
    return newExaInfras;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(exaInfras), JSON.stringify(subscriptions), JSON.stringify(favoriteItems)]);

  const onRefresh = (): void => {
    exaInfraRefresh();
  };

  const setSelectedItems = (selectedItems: ExaInfraItem[]): void => {
    setSelection(selectedItems);
  };

  const buildDeleteAction = (key:string, testId: string, selectedItem: ExaInfraItem)
  : CustomAction<ExaInfraItem | undefined> => {
    const deleteAction = {
      key,
      testId,
      disabled: !selectedItem,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      onClick: () => {
        trackActionClick(key, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
        triggerDeleteExaInfra({
          exaInfraId: selectedItem?.id || "",
          location: selectedLocation,
          onExecute: () => {
            exaInfraRefresh();
            listingComponentRef?.resetTableSelection();
          },
          onCancel: () => trackActionDiscard(key, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES),
        });
      },
    };
    return deleteAction;
  };

  const exaInfraRowActionMenus = (item?: ExaInfraItem | undefined): ActionType<ExaInfraItem>[] => {
    const exaInfraActionItems: ActionType<ExaInfraItem | undefined>[] = [];
    if (item) {
      if (item.isFavorite) {
        exaInfraActionItems.push({
          key: ActionMenuIds.RemoveFavorite,
          testId: ActionMenuTestIds.RemoveFavorite,
          text: Messages.actions.removeFromFavorites(),
          icon: MonochromeIconIds.FavoriteSet,
          onClick: () => {
            trackActionClick(ActionMenuIds.RemoveFavorite, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
            removeFromFavorites(item.id);
          },
        } as CustomAction<ExaInfraItem | undefined>);
      } else {
        exaInfraActionItems.push({
          key: ActionMenuIds.AddFavorite,
          testId: ActionMenuTestIds.AddFavorite,
          text: Messages.actions.addToFavorites(),
          icon: MonochromeIconIds.FavoriteNotSet,
          onClick: () => {
            trackActionClick(ActionMenuIds.AddFavorite, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
            setAsFavorite({ id: item.id, data: { location: item.location } } as FavoriteAccessItem);
          },
        } as CustomAction<ExaInfraItem | undefined>);
      }

      exaInfraActionItems.push(
        buildDeleteAction(ActionMenuIds.Delete, ActionMenuTestIds.Delete, item),
      );
    }
    return exaInfraActionItems;
  };

  const exaInfraBarActions: ActionType<ExaInfraItem>[] = [
    {
      key: ActionBarIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        trackActionClick(ActionBarIds.Create, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
        navigateTo(EXAINFRA_CREATE_ROUTE, PageId.EXAINFRA_CREATE, {
          analytics: {
            pageId: PageId.EXA_LIST_HUB,
            panelId: DetailsPanelId.INFRASTRUCTURES,
            actionName: ActionBarIds.Create,
          } as NavigationAnalyticsData,
        });
      },
    },
    {
      key: ActionBarIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      disabled,
      onClick: () => {
        trackActionClick(ActionBarIds.Refresh, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
        onRefresh(); listingComponentRef?.resetTableSelection();
      },
    },
    buildDeleteAction(ActionBarIds.Delete, ActionBarTestIds.Delete, selection[0]),
  ];

  React.useEffect(() => {
    const newSubscriptionOptions: { key: string, text: string }[] = [];
    // eslint-disable-next-line no-unused-expressions
    subscriptions && subscriptions.forEach(subscription => {
      newSubscriptionOptions.push({ key: subscription.id, text: subscription.name });
    });

    setSubscriptionOptions(newSubscriptionOptions);
    if (filterState.defaultSubscription) {
      setSelectedSubscription(filterState.defaultSubscription);
      if (defaultExtraFilters) defaultExtraFilters[0].value = filterState.defaultSubscription;
    } else if (preferredSubscription) {
      setSelectedSubscription(preferredSubscription);
    } else {
      setSelectedSubscription(newSubscriptionOptions.length ? newSubscriptionOptions[0].key : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subscriptions)]);

  const subscriptionFilter: ExtraFilter = {
    testId: FilterTestIds.SubscriptionFilter,
    name: columns[1].name,
    operator: Operator.Equal,
    value: filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
    fieldKey: columns[1].itemProp,
    customOptions: subscriptionOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedSubscription(value);
        filterState.setDefaultSubscription(value);
      }
    },
    hideDelete: true,
  };

  const resourceGroupFilter: ExtraFilter = {
    name: columns[2].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[2].itemProp)?.value || [FilterAll],
    fieldKey: columns[2].itemProp,
    customOptions: getFieldCustomOptions(items?.map(item => item.resourceGroupName), (fieldVal: string) => fieldVal),
  };

  React.useEffect(() => {
    if (filterState.defaultLocation) {
      setSelectedLocation(filterState.defaultLocation);
      if (defaultExtraFilters) defaultExtraFilters[1].value = filterState.defaultLocation;
    } else {
      setSelectedLocation(filterState.pillFilterStateValue.filterValues?.[1]?.value || selectedLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationFilter: ExtraFilter = {
    testId: FilterTestIds.LocationFilter,
    name: columns[3].name,
    operator: Operator.Equal,
    value: filterState.defaultLocation ? filterState.defaultLocation : selectedLocation,
    fieldKey: columns[3].itemProp,
    customOptions: locationOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedLocation(value);
        filterState.setDefaultLocation(value);
      }
    },
    hideDelete: true,
  };

  const lifecycleStateFilter: ExtraFilter = {
    name: columns[4].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[4].itemProp)?.value || [FilterAll],
    fieldKey: columns[4].itemProp,
    messageCallBack: stateT,
    customOptions: getFieldCustomOptions(
      items?.map(item => item.status || ""),
      (fieldVal: string) => {
        if (fieldVal) {
          return stateT(fieldVal);
        }
        return emptyChar;
      },
    ),
  };

  const allFilterPills = [
    subscriptionFilter,
    locationFilter,
    resourceGroupFilter,
    lifecycleStateFilter,
  ];

  const filterPills: ExtraFilter[] = [
    subscriptionFilter,
    locationFilter,
  ];

  const textFilterCallBack = (rowItem: ExaInfraItem): ExaInfraItem => {
    const displayRowItem = { ...rowItem };
    displayRowItem.status = stateT(
      displayRowItem.status || "",
    ) as CloudExadataInfrastructureSummaryStatusEnum;
    displayRowItem.location = getAzureLocationName(displayRowItem.location);
    return displayRowItem;
  };

  React.useEffect(() => {
    setFilterText(filterTextValue);
    filterState.setFilterTextValue(filterTextValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTextValue]);

  const defaultExtraFilters = useFilterStatePills(pillFilterValues, allFilterPills, listId, filterPills);

  const resetFilterValues = (): void => {
    const defaultPillValues = filterPills.map(filter => ({ fieldKey: filter.fieldKey, value: filter.value as string }));
    filterState.setFilterStateValue({ filterValues: defaultPillValues, listId, defaultPills: filterPills });
    filterState.setFilterTextValue("");
    setFilterText("");
  };

  return (
    <Listing
      testId={ListingTestIds.ExaInfra}
      listingComponentRef={setListingComponentRef}
      items={items}
      listColumns={columns}
      itemsPerPage={perPageItemsCount}
      actionBarItems={exaInfraBarActions as ActionType<undefined>[]}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      actions={exaInfraRowActionMenus}
      emptyList={{
        title: Messages.listExaInfra.emptyList.title(),
        emptyButtons: [
          {
            type: EmptyListingButtonTypes.PRIMARY,
            text: Messages.actions.createExaInfra(),
            action: () => navigateTo(EXAINFRA_CREATE_ROUTE, PageId.EXAINFRA_CREATE),
          },
        ],
        watermark: SvgIconIds.infraWaterMarkSvg,
      }}
      isLoading={(exaInfraLoading || subscriptionLoading) && !disabled}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.DisplayName,
      }}
      groupingSelect={{ groupingOptions }}
      viewSelect={{
        viewOptions,
        defaultSelectedKey: ViewKeys.ListView,
      }}
      filtering={{
        allExtraFilters: allFilterPills,
        defaultExtraFilters,
        defaultFilterText: filterText,
        textFilterCallBack,
        componentRef: setFilterComponentRef,
        onClearFilters: resetFilterValues,
      }}
    />
  );
};
